import { readUsers, type IFilter } from "@/api";
import { AdminPageHeader } from "@/components/admin";
import { UserFormDialog } from "@/components/admin/dialogs/new-user-dialog/UserFormDialog";
import { Button } from "@/components/ui/button";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMountEffect } from "@react-hookz/web";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type {
    PaginationState,
    RowSelectionState,
    SortingState,
} from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";

import { UserTable } from "./components/UserTable";
import { UserTableToolbarActions } from "./components/UserTableToolbarActions";
import { UsersToolbar } from "./components/UsersToolbar";

function buildSortFilter(sorting: SortingState): IFilter {
    return sorting.length > 0
        ? { sort_by: sorting[0].id, order: sorting[0].desc ? "desc" : "asc" }
        : {};
}

const AdminUsers = () => {
    const { setBreadcrumbs } = useBreadcrumb();
    useDocumentTitle("Users");
    useMountEffect(() => {
        setBreadcrumbs([{ label: "Admin", path: "home" }, { label: "Users" }]);
    });

    const [createOpen, setCreateOpen] = useState(false);

    // --- Filter state ---
    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();
    const [organizationId, setOrganizationId] = useState<number | null>(null);
    const [role, setRole] = useState<"admin" | "user" | null>(null);
    const [invitationStatus, setInvitationStatus] = useState<
        "accepted" | "pending" | null
    >(null);

    // --- Table state ---
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [sorting, setSorting] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    // Reset to page 0 when any filter or sort changes
    useEffect(() => {
        setPagination((prev) =>
            prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
        );
    }, [debouncedSearch, organizationId, role, invitationStatus, sorting]);

    // Clear selection whenever the visible result set changes. Page-change
    // alone (the MUI behavior) missed filter/sort changes made while already
    // on page 0, leaving invisible rows selected for "Delete Selected".
    useEffect(() => {
        setRowSelection({});
    }, [
        pagination.pageIndex,
        pagination.pageSize,
        debouncedSearch,
        organizationId,
        role,
        invitationStatus,
        sorting,
    ]);

    // Convert role string → is_admin boolean (null = no filter)
    const isAdmin = role === "admin" ? true : role === "user" ? false : null;

    // Convert invitation status string → invitation_accepted boolean (null = no filter)
    const invitationAccepted =
        invitationStatus === "accepted"
            ? true
            : invitationStatus === "pending"
              ? false
              : null;

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [
            "admin-users",
            pagination,
            debouncedSearch,
            organizationId,
            role,
            invitationStatus,
            sorting,
        ],
        queryFn: () =>
            readUsers(
                pagination.pageIndex + 1, // TanStack is 0-indexed; backend is 1-indexed
                pagination.pageSize,
                buildSortFilter(sorting),
                debouncedSearch || null,
                organizationId,
                isAdmin,
                invitationAccepted
            ),
        placeholderData: keepPreviousData,
    });

    // Prevent rowCount from flickering to 0 during refetch (keepPreviousData keeps rows
    // visible, but data?.total briefly becomes undefined — the ref holds the last value)
    const rowCountRef = useRef(data?.total ?? 0);
    const rowCount = useMemo(() => {
        if (data?.total !== undefined) rowCountRef.current = data.total;
        return rowCountRef.current;
    }, [data?.total]);

    // Clamp a past-the-end page (e.g. after deleting the last rows of the
    // last page) back to the last real page instead of showing an empty table.
    const isRecoveringPage = Boolean(
        data && data.page > Math.max(1, data.pages)
    );
    useEffect(() => {
        if (isRecoveringPage && data) {
            setPagination((prev) => ({
                ...prev,
                pageIndex: Math.max(1, data.pages) - 1,
            }));
        }
    }, [data, isRecoveringPage]);

    // Rows that leave the result set (a row-level delete, or an edit that moves a
    // user out of the active filter) must leave the selection too; otherwise
    // "Delete Selected" acts on users the admin can no longer see.
    useEffect(() => {
        if (!data) return;
        const present = new Set(data.items.map((user) => String(user.id)));
        setRowSelection((prev) => {
            const kept = Object.fromEntries(
                Object.entries(prev).filter(([id]) => present.has(id))
            );
            return Object.keys(kept).length === Object.keys(prev).length
                ? prev
                : kept;
        });
    }, [data]);

    const selectedUserIds = useMemo(
        () =>
            Object.keys(rowSelection)
                .filter((id) => rowSelection[id])
                .map(Number),
        [rowSelection]
    );

    const hasActiveFilters =
        !!debouncedSearch ||
        organizationId !== null ||
        role !== null ||
        invitationStatus !== null;

    const clearFilters = () => {
        handleSearchClear();
        setOrganizationId(null);
        setRole(null);
        setInvitationStatus(null);
    };

    return (
        <>
            <AdminPageHeader
                title="Users"
                subtitle="Manage accounts, roles, and organization memberships"
            >
                <div className="flex items-center gap-2">
                    <Button onClick={() => setCreateOpen(true)}>
                        Add New User
                    </Button>
                    <UserTableToolbarActions
                        selectedUserIds={selectedUserIds}
                        onDeleted={() => setRowSelection({})}
                    />
                </div>
            </AdminPageHeader>

            <div className="mt-6">
                <UsersToolbar
                    search={search}
                    onSearchChange={setSearch}
                    onSearchClear={handleSearchClear}
                    organizationId={organizationId}
                    onOrgIdChange={setOrganizationId}
                    role={role}
                    onRoleChange={setRole}
                    invitationStatus={invitationStatus}
                    onInvitationStatusChange={setInvitationStatus}
                />
                <UserTable
                    rows={data?.items ?? []}
                    rowCount={rowCount}
                    isLoading={isLoading}
                    isError={isError}
                    isRecoveringPage={isRecoveringPage}
                    hasActiveFilters={hasActiveFilters}
                    onClearFilters={clearFilters}
                    onRetry={() => refetch()}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                />
            </div>

            <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} />
        </>
    );
};

export default AdminUsers;
